'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Head from 'next/head';
import {
  Container,
  ContentWrapper,
  Card,
  Header,
  IconWrapper,
  Title,
  Subtitle,
  Form,
  FormGroup,
  Label,
  InputWrapper,
  Input,
  InputIcon,
  Button,
  Alert,
  ResultBox,
  ResultHeader,
  ResultInput,
  ResultDetail,
  LoadingContainer,
  Toast,
} from './styles';

const ShortenPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [url, setUrl] = useState('');
  const [shortUrl, setShortUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Decoder state
  const [decodeCode, setDecodeCode] = useState('');
  const [decodeResult, setDecodeResult] = useState<any>(null);
  const [decodeLoading, setDecodeLoading] = useState(false);
  const [decodeError, setDecodeError] = useState('');
  
  // Toast state
  const [showToast, setShowToast] = useState(false);

  // Hash function using SHA-256
  const hashPassword = async (password: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Cookie helpers
  const setCookie = (name: string, value: string, days: number) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
  };

  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  useEffect(() => {
    const checkPassword = async () => {
      const correctPasswordHash = process.env.NEXT_PUBLIC_SHORTEN_PASSWORD_HASH;
      
      // Check if we have a valid cookie
      const savedHash = getCookie('shorten_auth');
      if (savedHash === correctPasswordHash) {
        setAuthenticated(true);
        return;
      }

      // Prompt for password if no valid cookie
      const accessPassword = prompt('Enter password to access URL Shortener:');
      if (!accessPassword) {
        alert('Password required');
        window.location.href = '/';
        return;
      }

      const hashedInput = await hashPassword(accessPassword);
      
      if (hashedInput === correctPasswordHash) {
        setAuthenticated(true);
        // Save to cookie for 15 days
        setCookie('shorten_auth', hashedInput, 15);
      } else {
        alert('Incorrect password');
        window.location.href = '/';
      }
    };
    
    checkPassword();
  }, []);

  if (!authenticated) {
    return (
      <LoadingContainer>
        <p>Checking access...</p>
      </LoadingContainer>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShortUrl('');

    try {
      const response = await fetch('/api/shorten', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to shorten URL');
        return;
      }

      setShortUrl(data.shortUrl);
      setUrl('');
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shortUrl);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 10000);
  };

  const handleDecode = async (e: React.FormEvent) => {
    e.preventDefault();
    setDecodeLoading(true);
    setDecodeError('');
    setDecodeResult(null);

    try {
      const response = await fetch('/api/decode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: decodeCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        setDecodeError(data.error || 'Failed to decode');
        return;
      }

      setDecodeResult(data);
    } catch (err) {
      setDecodeError('An error occurred. Please try again.');
    } finally {
      setDecodeLoading(false);
    }
  };

  return (
    <>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <Container>
        <ContentWrapper>
          {/* Shortener Section */}
          <Card>
            <Header>
              <IconWrapper $color="blue">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </IconWrapper>
              <Title>Create Short URL</Title>
              <Subtitle>Transform long URLs into short, shareable links</Subtitle>
            </Header>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Long URL</Label>
                <InputWrapper>
                  <Input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com/very/long/url"
                    required
                  />
                  <InputIcon>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </InputIcon>
                </InputWrapper>
              </FormGroup>

              <Button type="submit" disabled={loading} $disabled={loading}>
                {loading ? (
                  <>
                    <svg className="spinning" fill="none" viewBox="0 0 24 24">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Shortening...
                  </>
                ) : (
                  <>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Shorten URL
                  </>
                )}
              </Button>
            </Form>

            {error && (
              <Alert $type="error">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p>{error}</p>
              </Alert>
            )}

            {shortUrl && (
              <Alert $type="success">
                <ResultBox>
                  <ResultHeader>
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Short URL Created!
                  </ResultHeader>
                  <ResultInput>
                    <input type="text" value={shortUrl} readOnly />
                    <button onClick={copyToClipboard}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </button>
                  </ResultInput>
                </ResultBox>
              </Alert>
            )}
          </Card>

          {/* Decoder Section */}
          <Card>
            <Header>
              <IconWrapper $color="purple">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </IconWrapper>
              <Title $gradient="linear-gradient(to right, #9333EA, #EC4899)">Decode Short URL</Title>
              <Subtitle>Reveal the original URL behind any short link</Subtitle>
            </Header>

            <Form onSubmit={handleDecode}>
              <FormGroup>
                <Label>Short Code</Label>
                <Input
                  type="text"
                  value={decodeCode}
                  onChange={(e) => setDecodeCode(e.target.value)}
                  placeholder="aBc123 or https://plaen.co/s/aBc123"
                  required
                  data-mono="true"
                />
              </FormGroup>

              <Button 
                type="submit" 
                disabled={decodeLoading} 
                $disabled={decodeLoading}
                $gradient="linear-gradient(to right, #9333EA, #EC4899)"
              >
                {decodeLoading ? (
                  <>
                    <svg className="spinning" fill="none" viewBox="0 0 24 24">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Decoding...
                  </>
                ) : (
                  <>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Decode
                  </>
                )}
              </Button>
            </Form>

            {decodeError && (
              <Alert $type="error">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p>{decodeError}</p>
              </Alert>
            )}

            {decodeResult && (
              <Alert $type="info">
                <ResultBox>
                  <ResultHeader data-purple="true">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Original URL Found
                  </ResultHeader>
                  <ResultDetail>
                    <a 
                      href={decodeResult.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {decodeResult.url}
                    </a>
                  </ResultDetail>
                  <ResultDetail style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                    <strong>Decoded at:</strong> {new Date(decodeResult.createdAt).toLocaleString()}
                  </ResultDetail>
                </ResultBox>
              </Alert>
            )}
          </Card>
        </ContentWrapper>
      </Container>
      {showToast && typeof window !== 'undefined' && createPortal(
        <Toast $isVisible={showToast}>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Copied to clipboard!
        </Toast>,
        document.body
      )}
    </>
  );
}

export default ShortenPage;